import type { SyntheticEvent } from 'react'
import React, { useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  Button,
  Color,
  Display,
  Divider,
  FlexDirection,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import {
  CALORIES_DISPLAY_PREFERENCE_TYPE,
  type MenuPreferences,
} from '@library/api-menu-experience'

import { useMenuPreferences } from 'routes/Menu/domains/menu'
import { useCustomisedRecipeTiles } from 'routes/Menu/hooks/useCustomisedRecipeTiles'
import { getUserFirstName } from 'selectors/user'
import { onEnter } from 'utils/accessibility'

import { DietaryPreferences } from './DietaryPreferences'
import { EquipmentPreferences } from './EquipmentPreferences'
import { PreferencesAlert } from './PreferencesAlert'
import { RecipeInformation } from './RecipeInformation'
import { RecipeInformationPreferences } from './RecipeInformationPreferences'

type MenuPreferencesFormProps = {
  onClose: (event: SyntheticEvent) => void
  onRefresh: () => void
}

const getLegacyDisplayPreferences = (menuPreferences: MenuPreferences): MenuPreferences => ({
  ...menuPreferences,
  display: {
    ...menuPreferences.display,
    list: menuPreferences.display.list.filter(
      (preference) => preference.type === CALORIES_DISPLAY_PREFERENCE_TYPE,
    ),
  },
})

export const MenuPreferencesForm = ({ onClose, onRefresh }: MenuPreferencesFormProps) => {
  const userFirstName = useSelector(getUserFirstName)
  const isCustomisedRecipeTilesEnabled = useCustomisedRecipeTiles()
  const { updateMenuPreferences, data, isLoading } = useMenuPreferences()

  const [formPreferences, setFormPreferences] = useState<MenuPreferences>({
    dietary: { list: [] },
    equipment: { list: [] },
    display: { list: [] },
  })

  const handleSave = async (event: SyntheticEvent) => {
    const updatedPreferences = isCustomisedRecipeTilesEnabled
      ? formPreferences
      : getLegacyDisplayPreferences(formPreferences)

    await updateMenuPreferences(updatedPreferences)
    onRefresh()
    onClose(event)
  }

  useEffect(() => {
    if (data) {
      setFormPreferences(data)
    }
  }, [data])

  return data ? (
    <Box height="100%" display={Display.Flex} flexDirection={FlexDirection.Column}>
      <Box
        alignItems={AlignItems.Center}
        bg={Color.White}
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        paddingH={6}
        paddingV={4}
      >
        <Heading3>{userFirstName}&apos;s preferences</Heading3>
        <Box
          data-testid="close-button"
          display={Display.Flex}
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={onEnter(onClose)}
        >
          <Icon name="close" size={6} variant={IconVariant.Actionable}>
            Close menu preferences
          </Icon>
        </Box>
      </Box>
      <Box paddingBottom={2}>
        <Divider />
      </Box>
      <Box paddingH={6} overflowY={Overflow.Scroll}>
        <PreferencesAlert />
        <DietaryPreferences
          dietaryPreferences={formPreferences.dietary.list}
          onChange={(dietaryPreferences) => {
            setFormPreferences({ ...formPreferences, dietary: { list: dietaryPreferences } })
          }}
        />
        <EquipmentPreferences
          equipmentPreferences={formPreferences.equipment.list}
          onChange={(equipmentPreferences) => {
            setFormPreferences({ ...formPreferences, equipment: { list: equipmentPreferences } })
          }}
        />
        {isCustomisedRecipeTilesEnabled ? (
          <RecipeInformationPreferences
            displayPreferences={formPreferences.display.list}
            maxSelectedDisplayPreferences={formPreferences.display.limit}
            onChange={(displayPreferences) =>
              setFormPreferences({
                ...formPreferences,
                display: { ...formPreferences.display, list: displayPreferences },
              })
            }
          />
        ) : (
          <RecipeInformation
            displayPreferences={formPreferences.display.list}
            onChange={(displayPreferences) =>
              setFormPreferences({
                ...formPreferences,
                display: { ...formPreferences.display, list: displayPreferences },
              })
            }
          />
        )}
      </Box>
      <Box
        bg={Color.White}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        paddingBottom={4}
      >
        <Box paddingBottom={4}>
          <Divider />
        </Box>
        <Box paddingH={6}>
          <Button
            data-testid="save-button"
            disabled={isLoading}
            height="48px"
            onClick={handleSave}
            width="100%"
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  ) : null
}
